import React, { useRef, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Dropdown } from '@factorialco/f0-react/dist/experimental'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { AcademicCap, Download } from '@factorialco/f0-react/icons/app'
import { JobCatalogTreePageDocument } from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import ConfirmationModal from 'modules/job_catalog/components/ConfirmationModal'
import { EmptyJobCatalogTree } from 'modules/job_catalog/components/EmptyJobCatalogTree/EmptyJobCatalogTree'
import JobCatalogTree from 'modules/job_catalog/components/JobCatalogTree'
import { useJobCatalogTreeVisibility } from 'modules/job_catalog/hooks/useJobCatalogTreeVisibility'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const LoadingSpinner = () => (
  <Box height="full" alignItems="center" justifyContent="center">
    <Spinner />
  </Box>
)

const JobCatalogTreePage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { data, loading } = useQuery(JobCatalogTreePageDocument)

  const { canSeeImport, canSeeCompetencies } = useJobCatalogTreeVisibility()

  const treeData = data?.jobCatalog.treesConnection.nodes[0]
  const canSeeJobCatalogTree = data?.jobCatalog._policies.jobCatalogTreeSee.can

  const canReimport = canSeeImport && !!data?.jobCatalog._policies.jobCatalogTreeReimport.can
  const canBulkAssignCompetencies = canSeeImport && canSeeCompetencies

  const navigateToImportJobCatalog = () => {
    navigate(navigation.employees.jobCatalog.importJobCatalogTree.fullPath())
  }

  const navigateToBulkAssignCompetencies = () => {
    navigate(navigation.employees.jobCatalog.bulkAssignCompetencies.fullPath())
  }

  const handleOpenReimportConfirmationModal = () => {
    setIsModalOpen(true)
  }

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current
        setDimensions({ width: offsetWidth, height: offsetHeight })
      }
    }
    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  const { width, height } = dimensions

  const renderJobCatalogTree = () => {
    if (loading) return <LoadingSpinner />
    if (treeData && width > 0 && height > 0)
      return <JobCatalogTree width={width} height={height} treeData={treeData} />

    return <EmptyJobCatalogTree />
  }

  return (
    <ResponsivePage.Body variant="fullscreen" authorized={canSeeJobCatalogTree}>
      <Box position="relative" width="full" height="full" ref={containerRef}>
        {(canReimport || canBulkAssignCompetencies) && (
          <Box position="absolute" style={{ top: '20px', right: '20px', zIndex: '1' }}>
            <Dropdown
              items={compact([
                canBulkAssignCompetencies && {
                  label: i18n.t('job_catalog.job_catalog_tree_competencies_assignment.menu.title'),
                  icon: AcademicCap,
                  description: i18n.t(
                    'job_catalog.job_catalog_tree_competencies_assignment.menu.description'
                  ),
                  visible: canBulkAssignCompetencies,
                  onClick: navigateToBulkAssignCompetencies,
                },
                canReimport && {
                  label: i18n.t('job_catalog.job_catalog_tree_reimport.menu.title'),
                  icon: Download,
                  description: i18n.t('job_catalog.job_catalog_tree_reimport.menu.description'),
                  visible: canReimport,
                  onClick: handleOpenReimportConfirmationModal,
                },
              ])}
            />
          </Box>
        )}
        {renderJobCatalogTree()}
        {isModalOpen && (
          <ConfirmationModal
            illustration="alert"
            title={i18n.t('job_catalog.job_catalog_tree_reimport.modal.title')}
            confirmText={i18n.t('job_catalog.job_catalog_tree_reimport.modal.confirm_button')}
            description={i18n.t('job_catalog.job_catalog_tree_reimport.modal.description')}
            onConfirm={navigateToImportJobCatalog}
            onCancel={() => setIsModalOpen(false)}
          />
        )}
        <React.Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </React.Suspense>
      </Box>
    </ResponsivePage.Body>
  )
}

export default JobCatalogTreePage
